<template>
    <div class="view--single view-map">
        <h1 class="section-title _shadowed _page-title">MAP</h1>
        <div id="the-map" class="map"></div>
        <div class="overlay">
            <div class="checkbox" @click="couponOnly = ! couponOnly">
                <div class="checkbox__box" :class="{ active_: couponOnly }"></div>
                <p class="checkbox__label">쿠폰 사용 가능한 곳만 보기</p>
            </div>
            <swiper class="swiper" ref="swiper" :options="swiperOptions" @slideChange="onSlideChange">
                <swiper-slide v-for="spot in spotsFiltered" :key="spot.id" class="swiper-slide">
                    <spot-card :selected="currentSpot && currentSpot.id === spot.id" :spot="spot"/>
                </swiper-slide>
            </swiper>
        </div>
        <div class="markers">
            <div v-for="spot in spots" :key="spot.id" :ref="spot.id"
                 v-show="spot.couponDiscount && couponOnly || ! couponOnly"
                 class="marker" :class="{ active_: currentSpot && currentSpot.id === spot.id }"
                 @click="selectSpot(spot)">
                <img src="~@/assets/pin.png" class="marker__body">
                <img v-if="spot.couponCond || spot.couponDiscount" src="~@/assets/pin-coupon.png" class="marker__coupon">
            </div>
        </div>
        <v-track title="Map"/>
    </div>
</template>

<script>
import 'mapbox-gl/dist/mapbox-gl.css'
import 'swiper/dist/css/swiper.css'

import ogImage from '@/assets/og.jpg'
import anime from 'animejs'
import mapbox from 'mapbox-gl'
import { swiper, swiperSlide } from 'vue-awesome-swiper/src/index'
import { store } from '@/stores'
import { getters, actions } from '@/stores/map'
import SpotCard from '@/components/SpotCard'

mapbox.accessToken = 'pk.eyJ1IjoidXJiYW5wbGF5IiwiYSI6ImNqZDQ2bGNjazB2OXIzM3FmM2o4dTl2YmUifQ.SnhG5qM_00GIu5wB4hRH8w'

export default {
    name: 'MapView',
    components: {
        swiper,
        swiperSlide,
        SpotCard
    },
    data() {
        return {
            map: null,
            markers: [],
            centered: false,
        }
    },
    computed: {
        couponOnly: {
            get() { return store.state.map.couponOnly },
            set(value) { store.dispatch(actions.setCouponOnly, value) }
        },
        currentSlide() {
            return store.state.map.currentSlide
        },
        currentSpot() {
            return this.spotsFiltered[this.currentSlide]
        },
        spots() {
            return store.state.map.items
        },
        spotsFiltered() {
            return store.getters[getters.itemsFiltered]
        },
        swiperOptions() {
            return {
                centeredSlides: true,
                slidesPerView: 'auto',
                spaceBetween: 15,
                initialSlide: this.currentSlide
            }
        }
    },
    metaInfo() {
        return {
            title: '오늘은 경리단 - MAP',
            meta: [
                { property: 'og:title', content: '오늘은 경리단 - MAP' },
                { property: 'og:description', content: '오늘은 경리단길에서 놀자! 경리단길 내 다양한 로컬 스팟을 중심으로 지역에서 활동하는 크리에이터들과 함께 동네를 자세히 보고 즐기는 특별한 기회. ‘오늘은 경리단’은 경리단 클래스, 경리단 쿠폰북, 경리단 굿즈 등의 프로그램을 통해 경리단길만의 특별한 콘텐츠를 소개합니다.', vmid: 'og:description' },
                { property: 'og:image', content: ogImage, vmid: 'og:image' },
                { property: 'og:image:alt', content: '오늘은 경리단 - MAP', vmid: 'og:image:alt'}
            ]
        }
    },
    methods: {
        drawMarkers() {
            this.markers.forEach(x => x.remove())
            this.markers = this.spotsFiltered.map(
                x => new mapbox.Marker(this.$refs[x.id][0])
                               .setLngLat(x.coords)
                               .addTo(this.map))
        },
        selectSpot(spot) {
            this.map.flyTo({
                center: spot.coords,
                offset: [0, -65]
            })
            if (this.centered) {
                const index = this.spotsFiltered.findIndex(x => x.id === spot.id)
                this.$refs.swiper.swiper.slideTo(index)
            }
        },
        async onSlideChange() {
            await store.dispatch(actions.setCurrentSlide, this.$refs.swiper.swiper.activeIndex)
            this.selectSpot(
                this.currentSpot)
        }
    },
    watch: {
        couponOnly() {
            this.$nextTick(() => {
                this.drawMarkers()
                this.selectSpot(this.spotsFiltered[0])
            })
        }
    },
    mounted() {
        const sw = new mapbox.LngLat(126.955271, 37.500957)
        const ne = new mapbox.LngLat(127.010457, 37.571929)
        const maxBounds = new mapbox.LngLatBounds(sw, ne)
        this.map = new mapbox.Map({
            container: 'the-map',
            style: 'mapbox://styles/urbanplay/cjie34qlz1xtk2rqotkhdvqqc',
            center: [126.992224, 37.539984],
            maxBounds,
            zoom: 17,
            minZoom: 14,
            maxZoom: 19
        })
        this.map.addControl(new mapbox.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true
            },
            trackUserLocation: true,
            showUserLocation: true
        }))
        this.$nextTick(() => {
            this.drawMarkers()
            this.selectSpot(this.currentSpot)
            this.centered = true
        })
        setTimeout(() => {
            anime({
                targets: this.$refs.swiper.$el,
                translateY: {
                    value: ['10%', 0],
                    duration: 750,
                },
                opacity: {
                    value: [0, 1],
                    easing: 'easeOutQuint',
                    duration: 350
                },
            })
        }, 200)
    },
    async beforeRouteEnter(_, __, next) {
        await store.dispatch(actions.fetchItems)
        next()
    }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_parts';

.section-title {
    color: #24154b;
    position: absolute;
    top: em(14, 18);
    right: 0;
    left: 0;
    z-index: 1;
}

.overlay {
    position: fixed;
    right: 0;
    bottom: em(55 + 18);
    left: 0;
    z-index: 2;
}
.checkbox {
    display: flex;
    align-items: center;
    padding-left: 20px;
}
.checkbox__box {
    width: em(24);
    height: em(24);
    box-shadow: 2px 2px 4px 0 rgba(32, 14, 98, 0.5);
    background: #fff;
    border: solid 1px #342264;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    &::before {
        content: '';
        width: em(16);
        height: em(16);
        background: #342264;
        opacity: 0;
        transform: scale(.7);
        transition: all 100ms $ease;
    }
    &.active_::before {
        opacity: 1;
        transform: scale(1);
        transition-timing-function: $ease-out;
    }
}
.checkbox__label {
    color: #342264;
    font-size: em(11);
    font-weight: 700;
    margin-left: em(7, 11);
    text-shadow: 0 0 1px #f8f8fc;
}

.swiper {
    opacity: 0;
    margin-top: em(8);
    will-change: opacity, transform;
}
.swiper-slide {
    width: em(335);
    min-width: em(335);
}
.spot-card {
    width: em(335);
    min-width: em(335);
}

.map {
    height: 100%;
}
.markers {
    visibility: hidden;
    position: absolute;
}
.marker {
    width: em(20, 12);
    height: em(30, 12);
    position: absolute;
    &.active_ {
        z-index: 1;
        .marker__body {
            filter: none;
        }
    }
}
.marker__body {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    filter: hue-rotate(43deg) brightness(2) saturate(.4);
}
.marker__coupon {
    width: em(15, 12);
    height: em(15, 12);
    display: block;
    position: absolute;
    top: em(-7);
    right: em(-9);
}
</style>